import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Dot, Loader2, MessageSquare, Pencil, PenLine, Trash2 } from 'lucide-react'
import { Note } from 'types/crm/notes'
import ContactEmptyState from '../ContactEmptyState'
import { ContactOwner } from 'types/crm/contacts'
import Spinner from 'components/Loaders/Spinner'
import { CrmCommentHooks } from 'types/crm/comments'
import CrmCommentsPanel from '../Comments/CrmCommentsPanel'
import ActionsDropdown from 'components/common/ActionsDropdown'
import { formatTimeIfToday } from 'utils/date';

interface NotesListProps {
    notes: Note[]
    isLoading?: boolean
    emptyMessage?: string
    hasMore?: boolean
    isFetchingMore?: boolean
    onLoadMore?: () => void
    onAddNote?: () => void
    onEditNote?: (note: Note) => void
    onDeleteNote?: (noteUuid: string) => void
    isFormOpen?: boolean
    owners: ContactOwner[]
    parentUuid: string
    commentHooks: CrmCommentHooks
    onCommentAdded?: () => void
}

interface NoteItemProps {
    note: Note
    owners: ContactOwner[]
    parentUuid: string
    commentHooks: CrmCommentHooks
    onCommentAdded?: () => void
    onEditNote?: (note: Note) => void
    onDeleteNote?: (noteUuid: string) => void
}

const NoteItem = ({ note, owners, parentUuid, commentHooks, onCommentAdded, onEditNote, onDeleteNote }: NoteItemProps) => {

    const [isExpanded, setIsExpanded] = useState(false)
    const [isNoteExpanded, setIsNoteExpanded] = useState(false)
    const [isNoteOverflowing, setIsNoteOverflowing] = useState(false)
    const noteTextRef = useRef<HTMLParagraphElement>(null)
    const commentCount = note.commentCount ?? 0

    useEffect(() => {
        const el = noteTextRef.current
        if (el) {
            setIsNoteOverflowing(el.scrollHeight > el.clientHeight)
        }
    }, [note.note])

    return (
        <div className="rounded-lg bg-primary-light p-4 space-y-3 overflow-hidden">
            <div className="flex items-start gap-3">
                {/* icon first, then accordion arrow */}
                <span className='p-1.5 rounded-lg bg-primary-variant'>
                    <PenLine className="w-4 h-4 text-color-accent" />
                </span>

                <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="flex items-center gap-1 text-xs text-accent hover:opacity-80 transition-opacity mt-1"
                >
                    {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                    ) : (
                        <ChevronRight className="w-4 h-4" />
                    )}
                </button>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <p
                                ref={noteTextRef}
                                className={`text-sm text-neutral whitespace-pre-wrap break-words capitalize ${isNoteExpanded ? '' : 'line-clamp-4'
                                    }`}
                            >
                                {note.note}
                            </p>

                            {isNoteOverflowing && (
                                <button
                                    type="button"
                                    onClick={() => setIsNoteExpanded((prev) => !prev)}
                                    className="text-xs text-color-accent hover:opacity-80 transition-opacity mt-1"
                                >
                                    {isNoteExpanded ? 'Show less' : 'Show more'}
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-neutral-faded">
                                {/* {formatRelativeTime(note.createdDate)} */}
                                {formatTimeIfToday(note.createdDate)}
                            </span>

                            {(onEditNote || onDeleteNote) && (
                                <ActionsDropdown
                                    size="sm"
                                    actions={[
                                        ...(onEditNote
                                            ? [{ label: 'Edit Note', icon: <Pencil className="w-4 h-4" />, onClick: () => onEditNote(note) }]
                                            : []),
                                        ...(onDeleteNote
                                            ? [{ label: 'Delete Note', icon: <Trash2 className="w-4 h-4" />, variant: 'danger' as const, onClick: () => onDeleteNote(note.uuid) }]
                                            : []),
                                    ]}
                                />
                            )}
                        </div>
                    </div>
                    <div className="flex items-center flex-wrap gap-0.5 mt-1">
                        {note.createdBy && (
                            <span className="text-xs text-neutral-faded">
                                By <span className="text-neutral-faded">{note.createdBy.name}</span>
                            </span>
                        )}

                        {note.createdBy && note.associatedPerson && (
                            <Dot className=" text-neutral-faded" />
                        )}

                        {note.associatedPerson && (
                            <span className="text-xs text-neutral-faded">
                                For: <span className="text-neutral-faded">{note.associatedPerson.name}</span>
                            </span>
                        )}

                        {(note.createdBy || note.associatedPerson) && (
                            <Dot className=" text-neutral-faded" />
                        )}

                        <button
                            type="button"
                            onClick={() => setIsExpanded((prev) => !prev)}
                            className="flex items-center gap-1 text-xs text-color-accent hover:opacity-80 transition-opacity"
                        >
                            <MessageSquare className="w-3 h-3 text-color-accent" />
                            {commentCount > 0
                                ? `${commentCount} comment${commentCount > 1 ? 's' : ''}`
                                : 'Add Comment'}
                        </button>
                    </div>
                </div>
            </div>
            {isExpanded && (
                <div className="pt-2 mb-4 border-t border-primary-subtle">
                    <CrmCommentsPanel
                        parentUuid={parentUuid}
                        entityUuid={note.uuid}
                        commentHooks={commentHooks}
                        mentionUsers={owners}
                        onCommentAdded={onCommentAdded}
                    />
                </div>
            )}
        </div>
    )
}

const NotesList = ({
    notes,
    isLoading,
    emptyMessage = 'No notes yet',
    hasMore = false,
    isFetchingMore = false,
    onLoadMore,
    onAddNote,
    onEditNote,
    onDeleteNote,
    isFormOpen = false,
    owners,
    parentUuid,
    commentHooks,
    onCommentAdded
}: NotesListProps) => {
    const sentinelRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!hasMore || !onLoadMore) return
        const node = sentinelRef.current
        if (!node) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    onLoadMore()
                }
            },
            { threshold: 0.1 },
        )

        observer.observe(node)
        return () => observer.disconnect()
    }, [hasMore, onLoadMore])

    if (isLoading) {
        return <Spinner />
    }

    if (!notes || notes.length === 0) {
        if (isFormOpen) return null

        return (
            <ContactEmptyState
                icon={PenLine}
                title="No notes yet"
                description="Log calls, meetings, and context so your team stays aligned."
                action={onAddNote ? { label: 'Add note', onClick: onAddNote } : undefined}
            />
        )
    }
    return (
        <div className="space-y-3">
            {notes.map((note) => (
                <NoteItem
                    key={note.uuid}
                    note={note}
                    owners={owners}
                    parentUuid={parentUuid}
                    commentHooks={commentHooks}
                    onCommentAdded={onCommentAdded}
                    onEditNote={onEditNote}
                    onDeleteNote={onDeleteNote}
                />
            ))}

            <div className="flex flex-col items-center gap-2 py-2">
                {isFetchingMore && (
                    <Loader2 className="w-5 h-5 animate-spin text-neutral-medium" />
                )}
                {hasMore && <div ref={sentinelRef} className="h-1 w-full" />}
            </div>
        </div>
    )
}
export default NotesList